import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useFloating, offset, flip, shift, autoUpdate } from "@floating-ui/react-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Navigate } from "react-router-dom";
import { fetchAllTeams } from "@/lib/fetchAllTeams";
import {
  LayoutGrid, Trophy, Building2, Users, Film, Calendar, Video, Download,
  Search, ArrowUpDown, ArrowUp, ArrowDown, FileDown, DollarSign, UserCheck, Shuffle, X, Eye, EyeOff,
  Heart, Briefcase, ClipboardList, CheckCircle2, Mail, Phone, RotateCcw, Trash2, Megaphone, Image as ImageIcon, Upload as UploadIcon,
  Webhook, RefreshCw, AlertTriangle, FileText, Copy, Pencil,
} from "lucide-react";

/* ─── Excel-style DataGrid ─── */
const DataGrid = ({ columns, rows, keyField = "id", empty = "No data", rowActions, testidPrefix }) => {
  const [sort, setSort] = useState({ key: null, dir: "asc" });
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(r => columns.some(c => {
      const v = c.accessor ? c.accessor(r) : r[c.key];
      return String(v ?? "").toLowerCase().includes(q);
    }));
  }, [rows, query, columns]);

  const sorted = useMemo(() => {
    if (!sort.key) return filtered;
    const col = columns.find(c => c.key === sort.key);
    if (!col) return filtered;
    const getVal = (r) => col.accessor ? col.accessor(r) : r[col.key];
    return [...filtered].sort((a, b) => {
      const va = getVal(a), vb = getVal(b);
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      const cmp = typeof va === "number" && typeof vb === "number"
        ? va - vb
        : String(va).localeCompare(String(vb), undefined, { numeric: true });
      return sort.dir === "asc" ? cmp : -cmp;
    });
  }, [filtered, sort, columns]);

  const toggleSort = (key) => {
    setSort(s => s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" });
  };

  const exportCsv = () => {
    const header = columns.map(c => `"${c.label.replace(/"/g, '""')}"`).join(",");
    const lines = sorted.map(r => columns.map(c => {
      const v = c.accessor ? c.accessor(r) : r[c.key];
      return `"${String(v ?? "").replace(/"/g, '""')}"`;
    }).join(","));
    const blob = new Blob([header + "\n" + lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `${testidPrefix || "export"}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div data-testid={testidPrefix ? `${testidPrefix}-grid` : undefined}>
      <div style={{ display: 'flex', gap: 10, marginBottom: 12, alignItems: 'center' }}>
        <div style={{ position: 'relative', flex: 1, maxWidth: 380 }}>
          <Search size={14} style={{ position: 'absolute', left: 12, top: 12, color: '#666' }} />
          <input
            data-testid={testidPrefix ? `${testidPrefix}-search` : undefined}
            placeholder="Search…"
            value={query}
            onChange={e => setQuery(e.target.value)}
            style={{
              width: '100%', padding: '9px 12px 9px 34px',
              background: '#0c0c10', border: '1px solid var(--dark-border)',
              borderRadius: 8, color: '#fff', fontSize: 13,
              fontFamily: "'DM Sans', sans-serif",
            }}
          />
        </div>
        <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{sorted.length} row{sorted.length === 1 ? "" : "s"}</div>
        <button data-testid={testidPrefix ? `${testidPrefix}-export` : undefined} onClick={exportCsv} style={{
          padding: '8px 14px', background: 'rgba(0,229,255,0.1)', border: '1px solid rgba(0,229,255,0.35)',
          borderRadius: 8, color: 'var(--cyan)', cursor: 'pointer', fontSize: 12, fontWeight: 600,
          display: 'inline-flex', alignItems: 'center', gap: 6, fontFamily: "'DM Sans', sans-serif",
        }}><FileDown size={13} /> Export CSV</button>
      </div>

      <div style={{
        overflow: 'auto', border: '1px solid var(--dark-border)', borderRadius: 10,
        background: '#0a0a0d',
        maxHeight: 'calc(100vh - 280px)',
      }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 760, fontSize: 13 }}>
          <thead style={{ position: 'sticky', top: 0, background: '#121217', zIndex: 2 }}>
            <tr>
              {columns.map(c => (
                <th key={c.key} onClick={() => toggleSort(c.key)} style={{
                  textAlign: 'left', padding: '10px 12px',
                  borderBottom: '1px solid var(--dark-border)',
                  fontWeight: 700, color: 'var(--text-secondary)',
                  fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5,
                  cursor: 'pointer', userSelect: 'none',
                  whiteSpace: 'nowrap',
                }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    {c.label}
                    {sort.key === c.key
                      ? (sort.dir === "asc" ? <ArrowUp size={12} /> : <ArrowDown size={12} />)
                      : <ArrowUpDown size={11} style={{ opacity: 0.3 }} />}
                  </span>
                </th>
              ))}
              {rowActions && <th style={{ padding: '10px 12px', borderBottom: '1px solid var(--dark-border)' }}>&nbsp;</th>}
            </tr>
          </thead>
          <tbody>
            {sorted.length === 0 ? (
              <tr><td colSpan={columns.length + (rowActions ? 1 : 0)} style={{ padding: 36, textAlign: 'center', color: 'var(--text-secondary)' }}>{empty}</td></tr>
            ) : sorted.map(r => (
              <tr key={r[keyField]} data-testid={testidPrefix ? `${testidPrefix}-row-${r[keyField]}` : undefined} style={{ transition: 'background 0.12s ease' }}
                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.02)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>
                {columns.map(c => {
                  const raw = c.accessor ? c.accessor(r) : r[c.key];
                  return (
                    <td key={c.key} style={{ padding: '10px 12px', borderBottom: '1px solid rgba(255,255,255,0.04)', color: '#e6e6ea', whiteSpace: c.nowrap ? 'nowrap' : undefined }}>
                      {c.render ? c.render(r) : (raw ?? "")}
                    </td>
                  );
                })}
                {rowActions && <td style={{ padding: '8px 12px', borderBottom: '1px solid rgba(255,255,255,0.04)', whiteSpace: 'nowrap' }}>{rowActions(r)}</td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

/* ─── KPI card ─── */
const KPI = ({ label, value, icon: Icon, color = "var(--cyan)", testid }) => (
  <div data-testid={testid} style={{
    background: '#0c0c10', border: '1px solid var(--dark-border)', borderRadius: 12,
    padding: '16px 18px', display: 'flex', alignItems: 'center', gap: 14, minWidth: 180, flex: 1,
  }}>
    <div style={{ width: 42, height: 42, borderRadius: 10, background: `${color}22`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Icon size={20} style={{ color }} />
    </div>
    <div>
      <p style={{ fontSize: 11, color: 'var(--text-secondary)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 2 }}>{label}</p>
      <p style={{ fontSize: 22, fontWeight: 800, fontFamily: "'Outfit', sans-serif", color: '#fff', lineHeight: 1 }}>{value}</p>
    </div>
  </div>
);

/* ─── Detail row used inside the application modal ─── */
const DetailRow = ({ icon: Icon, label, value, stack = false }) => (
  <div style={{ display: stack ? 'block' : 'flex', alignItems: 'flex-start', gap: 12, padding: '8px 0', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text-secondary)', fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, minWidth: stack ? undefined : 180, marginBottom: stack ? 6 : 0 }}>
      <Icon size={13} /> {label}
    </div>
    <div style={{ color: '#fff', fontSize: 14, lineHeight: 1.55, whiteSpace: 'pre-wrap', flex: 1 }}>{value}</div>
  </div>
);

/* ─── Reusable action-button style ─── */
const appBtn = (color) => {
  const palette = {
    cyan: { c: "var(--cyan)", bg: "rgba(0,229,255,0.12)", b: "rgba(0,229,255,0.35)" },
    green: { c: "#4caf50", bg: "rgba(76,175,80,0.12)", b: "rgba(76,175,80,0.35)" },
    orange: { c: "#ff6b00", bg: "rgba(255,107,0,0.12)", b: "rgba(255,107,0,0.35)" },
    red: { c: "#f44336", bg: "rgba(244,67,54,0.12)", b: "rgba(244,67,54,0.35)" },
  };
  const p = palette[color] || palette.cyan;
  return {
    padding: '5px 10px', background: p.bg, border: `1px solid ${p.b}`,
    borderRadius: 8, color: p.c, fontSize: 11, fontWeight: 700, cursor: 'pointer',
    display: 'inline-flex', alignItems: 'center', gap: 4,
    fontFamily: "'DM Sans', sans-serif",
  };
};

/* ─── Main page ─── */
const MainframePage = () => {
  const { user, api } = useAuth();
  const [tab, setTab] = useState("overview");
  const [overview, setOverview] = useState(null);
  const [leagues, setLeagues] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [teams, setTeams] = useState([]);
  const [users, setUsers] = useState([]);
  const [games, setGames] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [downloading, setDownloading] = useState(null);
  // Per-row "⋯" actions menu for the games grid. Positioning is delegated to
  // Floating UI (anchored bottom-end to the button, flip/shift for collisions,
  // autoUpdate tracks the button through grid scroll/resize) — hand-rolled
  // coordinate math clipped and visually detached from its row. {row} | null.
  const [gameMenu, setGameMenu] = useState(null); // {row, buttonEl} | null
  const { refs: gameMenuRefs, floatingStyles: gameMenuStyles } = useFloating({
    strategy: "fixed",
    placement: "bottom-end",
    middleware: [offset(2), flip({ padding: 8 }), shift({ padding: 8 })],
    whileElementsMounted: autoUpdate,
    // Reference comes from state (the clicked ⋯), not refs.setReference — the
    // hook then re-measures on mount/re-render instead of trusting a stale rect.
    elements: { reference: gameMenu?.buttonEl },
  });
  // index.css applies a global `zoom: 1.09` (body). CSS zoom multiplies the
  // menu's own position offsets at paint time, so everything Floating UI
  // computes lands ~9% down-right of the button. Neutralize it with a zoom
  // sandwich: the floating element carries `zoom: 1/bodyZoom` (its offsets then
  // apply 1:1 with the visual coordinates Floating UI measured) and the menu
  // CONTENT re-applies bodyZoom so it renders at app scale.
  const bodyZoom = parseFloat(typeof window !== "undefined" ? getComputedStyle(document.body).zoom : "1") || 1;
  const [reassignTarget, setReassignTarget] = useState(null); // game being reassigned
  const [reassignSide, setReassignSide] = useState("home"); // "home" | "away"
  const [reassignTeamId, setReassignTeamId] = useState("");
  const [reassigning, setReassigning] = useState(false);
  const [reassignError, setReassignError] = useState(null);
  const [advisoryApps, setAdvisoryApps] = useState([]);
  const [gdoApps, setGdoApps] = useState([]);
  const [signups, setSignups] = useState([]);
  const [activeApp, setActiveApp] = useState(null);
  // Full GDO pre-screening viewer — opens a modal with the sectioned
  // pre-screening answers so reviewers can triage without leaving Mainframe.
  const [preScreeningView, setPreScreeningView] = useState(null);
  // User management (users tab)
  const [pendingUsers, setPendingUsers] = useState([]);
  const [editUser, setEditUser] = useState(null);
  const [resetPwUser, setResetPwUser] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null); // {title, message, onConfirm, tone, busyLabel}
  const [actionFeedback, setActionFeedback] = useState(null); // {message, tone}

  const refreshUsers = () => {
    api().get("/mainframe/users").then(r => setUsers(r.data)).catch(() => {});
    api().get("/mainframe/approvals/pending").then(r => setPendingUsers(r.data)).catch(() => {});
    api().get("/mainframe/overview").then(r => setOverview(r.data)).catch(() => {});
  };

  useEffect(() => {
    if (!user || user.role !== "ysv_admin") return;
    api().get("/mainframe/overview").then(r => setOverview(r.data)).catch(() => {});
    api().get("/leagues").then(r => setLeagues(r.data)).catch(() => {});
    api().get("/organizations").then(r => setOrgs(r.data)).catch(() => {});
    fetchAllTeams(api()).then(setTeams).catch(() => {});
    api().get("/mainframe/users").then(r => setUsers(r.data)).catch(() => {});
    api().get("/mainframe/approvals/pending").then(r => setPendingUsers(r.data)).catch(() => {});
    api().get("/mainframe/games").then(r => setGames(r.data)).catch(() => {});
    api().get("/schedules").then(r => setSchedules(r.data)).catch(() => {});
    api().get("/mainframe/advisory-board-applications").then(r => setAdvisoryApps(r.data)).catch(() => {});
    api().get("/mainframe/gdo-applications").then(r => setGdoApps(r.data)).catch(() => {});
    api().get("/mainframe/league-org-signups").then(r => setSignups(r.data)).catch(() => {});
  }, [api, user]);

  if (!user) return null;
  if (user.role !== "ysv_admin") return <Navigate to="/home" replace />;

  const downloadGame = async (g) => {
    setDownloading(g.id);
    try {
      const token = localStorage.getItem("ysv_token");
      // Trailing || "game" mirrors the server's safe_title fallback: a title of
      // only stripped characters ("!!!") must not produce a ".mp4" filename.
      const safe = (g.title || "game").replace(/[^a-z0-9_\- ]/gi, "").trim().replace(/\s+/g, "_") || "game";
      const fetchOne = async (query, filename) => {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/games/${g.id}/download${query}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error("Download failed");
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        a.click();
        URL.revokeObjectURL(url);
      };
      const segments = g.segments || [];
      if (segments.length > 0) {
        // Multi-part game: each quarter/half lives on its own segment row —
        // the parent's video_url is only the first upload, so fetch every part.
        for (const seg of segments) {
          const label = (seg.label || "part").replace(/[^a-z0-9_\-]/gi, "");
          const ext = (seg.video_url || "").split(".").pop() || "mp4";
          await fetchOne(`?segment_id=${seg.id}`, `${safe}_${label}.${ext}`);
        }
      } else {
        const ext = (g.video_url || "").split(".").pop() || "mp4";
        await fetchOne("", `${safe}.${ext}`);
      }
    } catch (e) {
      alert("Download failed: " + e.message);
    } finally {
      setDownloading(null);
    }
  };

  const toggleGameMenu = (e, row) => {
    const buttonEl = e.currentTarget;
    setGameMenu(m => (m?.row.id === row.id ? null : { row, buttonEl }));
  };
  const gameMenuAction = (fn) => { const row = gameMenu.row; setGameMenu(null); fn(row); };

  const renameGame = async (g) => {
    const next = window.prompt("New game title:", g.title || "");
    if (next == null) return; // cancelled
    const title = next.trim();
    if (!title || title === g.title) return;
    try {
      const r = await api().patch(`/games/${g.id}/title`, { title });
      setGames(list => list.map(x => (x.id === g.id ? { ...x, title: r.data.title } : x)));
    } catch (e) {
      alert(e.response?.data?.detail || "Rename failed");
    }
  };

  const counts = (u_list, team_id) => u_list.filter(u => u.team_id === team_id).length;
  const orgCounts = (u_list, org_id) => u_list.filter(u => u.org_id === org_id).length;

  const openReassign = (game, side = "home") => {
    setReassignTarget(game);
    setReassignSide(side);
    setReassignTeamId(side === "home" ? (game.team_id || "") : (game.away_team_id || ""));
    setReassignError(null);
  };
  const closeReassign = () => {
    setReassignTarget(null);
    setReassignSide("home");
    setReassignTeamId("");
    setReassignError(null);
  };
  const submitReassign = async () => {
    if (!reassignTarget) return;
    setReassigning(true);
    setReassignError(null);
    try {
      const body = reassignSide === "home"
        ? { team_id: reassignTeamId || null, side: "home" }
        : { away_team_id: reassignTeamId || null, side: "away" };
      await api().patch(`/games/${reassignTarget.id}/reassign`, body);
      const g = await api().get("/mainframe/games");
      setGames(g.data);
      closeReassign();
    } catch (e) {
      setReassignError(e.response?.data?.detail || "Reassign failed");
    } finally {
      setReassigning(false);
    }
  };
  const removeFromPortal = async (game, side = "home") => {
    const teamLabel = side === "home" ? (game.team_name || "the home team") : (game.away_team_name || "the away team");
    if (!window.confirm(`Remove "${game.title}" from ${teamLabel}'s portal? It will stay in the Mainframe (YSV-only).`)) return;
    try {
      const body = side === "home"
        ? { team_id: null, side: "home" }
        : { away_team_id: null, side: "away" };
      await api().patch(`/games/${game.id}/reassign`, body);
      const g = await api().get("/mainframe/games");
      setGames(g.data);
    } catch (e) {
      alert("Remove failed: " + (e.response?.data?.detail || e.message));
    }
  };

  /* ═══ User management (mainframe) ═══ */
  const showFeedback = (message, tone = "success") => {
    setActionFeedback({ message, tone });
    setTimeout(() => setActionFeedback(null), 4000);
  };
  const decideApproval = async (u, action) => {
    try {
      await api().post(`/mainframe/approvals/${u.id}`, { action });
      refreshUsers();
      showFeedback(`${action === "approve" ? "Approved" : "Rejected"} ${u.name || u.email}`, action === "approve" ? "success" : "warn");
    } catch (e) {
      showFeedback((action === "approve" ? "Approve" : "Reject") + " failed: " + (e.response?.data?.detail || e.message), "error");
    }
  };
  const changeUserRole = (u, new_role) => {
    if (u.id === user.id) { showFeedback("You cannot change your own role.", "error"); return; }
    setConfirmAction({
      title: "Change role?",
      message: `Set ${u.name || u.email}'s role from ${(u.role || "").replace("_", " ")} → ${new_role.replace("_", " ")}.`,
      tone: "cyan",
      busyLabel: "Updating…",
      onConfirm: async () => {
        await api().patch(`/users/${u.id}/role`, { role: new_role });
        refreshUsers();
        showFeedback(`Role changed to ${new_role.replace("_", " ")}`, "success");
      },
    });
  };
  const deleteUser = (u) => {
    if (u.id === user.id) { showFeedback("You cannot delete your own account.", "error"); return; }
    setConfirmAction({
      title: "Delete user?",
      message: `Permanently delete ${u.name || u.email}. This cannot be undone. Clips and highlights they authored are preserved.`,
      tone: "red",
      busyLabel: "Deleting…",
      onConfirm: async () => {
        await api().delete(`/mainframe/users/${u.id}`);
        refreshUsers();
        showFeedback(`Deleted ${u.name || u.email}`, "warn");
      },
    });
  };
  const toggleSubscription = (u) => {
    const next = u.subscription_status === "active" ? "inactive" : "active";
    setConfirmAction({
      title: next === "active" ? "Activate subscription?" : "Deactivate subscription?",
      message: `Set ${u.name || u.email}'s YSV Member subscription to ${next.toUpperCase()}. They will ${next === "active" ? "regain" : "lose"} access to games, clips, and highlights immediately.`,
      tone: next === "active" ? "green" : "orange",
      busyLabel: next === "active" ? "Activating…" : "Deactivating…",
      onConfirm: async () => {
        await api().post(`/mainframe/users/${u.id}/subscription`, { status: next });
        refreshUsers();
        showFeedback(`Subscription set to ${next.toUpperCase()}`, "success");
      },
    });
  };
  const syncSubscription = async (u) => {
    showFeedback(`Checking Stripe for ${u.name || u.email}…`, "info");
    try {
      const r = await api().post(`/mainframe/users/${u.id}/sync-subscription`);
      const d = r.data || {};
      if (d.synced_to_active) {
        showFeedback(`✅ Stripe confirmed payment — ${u.name || u.email} is now ACTIVE.`, "success");
      } else {
        showFeedback(`⚠️ Stripe shows payment_status="${d.stripe_payment_status}". No change made — use the Activate button to override.`, "warn");
      }
      refreshUsers();
    } catch (e) {
      showFeedback("Sync failed: " + (e.response?.data?.detail || e.message), "error");
    }
  };

  const tabs = [
    { key: "overview", label: "Overview", icon: LayoutGrid },
    { key: "leagues", label: "Leagues", icon: Trophy },
    { key: "orgs", label: "Organizations", icon: Building2 },
    { key: "teams", label: "Teams", icon: Users },
    { key: "users", label: "Users", icon: UserCheck },
    { key: "approvals", label: `Approvals${pendingUsers.length > 0 ? ` (${pendingUsers.length})` : ''}`, icon: UserCheck },
    { key: "games", label: "Games", icon: Film },
    { key: "schedules", label: "Schedules", icon: Calendar },
    { key: "gdo", label: "GDO", icon: Video },
    { key: "advisory", label: "Advisory Board", icon: Heart },
    { key: "gdo-apps", label: "GDO Apps", icon: Briefcase },
    { key: "signups", label: "Signups", icon: ClipboardList },
    { key: "promo", label: "Promo Banner", icon: Megaphone },
    { key: "webhooks", label: "Webhooks", icon: Webhook },
  ];

  /* ─── Application status helpers ─── */
  const STATUS_STYLES = {
    new: { color: "#ff6b00", bg: "rgba(255,107,0,0.12)" },
    contacted: { color: "#4caf50", bg: "rgba(76,175,80,0.12)" },
    dismissed: { color: "#888", bg: "rgba(136,136,136,0.12)" },
  };
  const StatusPill = ({ status }) => {
    const s = STATUS_STYLES[status] || STATUS_STYLES.new;
    return (
      <span style={{
        padding: '3px 10px', borderRadius: 12, fontSize: 11, fontWeight: 700,
        textTransform: 'uppercase', letterSpacing: 0.4,
        color: s.color, background: s.bg,
      }}>{status || "new"}</span>
    );
  };

  const updateAppStatus = async (kind, appId, status) => {
    const route = {
      advisory: "/mainframe/advisory-board-applications",
      "gdo-apps": "/mainframe/gdo-applications",
      signups: "/mainframe/league-org-signups",
    }[kind];
    if (!route) return;
    try {
      const r = await api().patch(`${route}/${appId}`, { status });
      const updated = r.data;
      const setter = kind === "advisory" ? setAdvisoryApps : kind === "gdo-apps" ? setGdoApps : setSignups;
      setter(prev => prev.map(a => a.id === appId ? updated : a));
      if (activeApp?.app?.id === appId) setActiveApp({ kind, app: updated });
    } catch (e) {
      alert("Update failed: " + (e.response?.data?.detail || e.message));
    }
  };

  return (
    <div data-testid="mainframe-page" style={{
      padding: '24px 28px', fontFamily: "'DM Sans', sans-serif",
    }}>
      <div style={{ marginBottom: 20, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <p style={{ fontSize: 11, color: '#ff6b00', fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase' }}>YSV Super Admin · Desktop Only</p>
          <h1 style={{ fontFamily: "'Outfit', sans-serif", fontSize: 32, fontWeight: 800, color: '#fff', marginTop: 4 }}>Mainframe Dashboard</h1>
          <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 4 }}>Executive analytics, full data grids, GDO management, and game download tools.</p>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 2, borderBottom: '1px solid var(--dark-border)', marginBottom: 20, overflowX: 'auto' }}>
        {tabs.map(t => (
          <button key={t.key} data-testid={`mf-tab-${t.key}`} onClick={() => setTab(t.key)} style={{
            padding: '10px 16px', background: 'none', border: 'none',
            color: tab === t.key ? 'var(--cyan)' : 'var(--text-secondary)',
            fontSize: 13, fontWeight: tab === t.key ? 700 : 500,
            borderBottom: tab === t.key ? '2px solid var(--cyan)' : '2px solid transparent',
            marginBottom: -1, cursor: 'pointer',
            display: 'inline-flex', alignItems: 'center', gap: 6,
            whiteSpace: 'nowrap',
          }}><t.icon size={14} /> {t.label}</button>
        ))}
      </div>

      {/* ══ OVERVIEW ══ */}
      {tab === "overview" && (
        <div data-testid="mf-overview">
          {!overview ? (
            <p style={{ color: 'var(--text-secondary)' }}>Loading KPIs…</p>
          ) : (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 14 }}>
                <KPI testid="mf-kpi-leagues" label="Leagues" value={overview.totals.leagues} icon={Trophy} />
                <KPI testid="mf-kpi-orgs" label="Organizations" value={overview.totals.organizations} icon={Building2} />
                <KPI testid="mf-kpi-teams" label="Teams" value={overview.totals.teams} icon={Users} color="#ff6b00" />
                <KPI testid="mf-kpi-users" label="Total Users" value={overview.totals.users} icon={UserCheck} color="#ce93d8" />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 14 }}>
                <KPI testid="mf-kpi-games" label="Games" value={overview.totals.games} icon={Film} color="#4caf50" />
                <KPI testid="mf-kpi-schedules" label="Schedules" value={overview.totals.schedules} icon={Calendar} color="#00e5ff" />
                <KPI testid="mf-kpi-gdos" label="GDOs" value={overview.totals.gdos} icon={Video} color="#9c27b0" />
                <KPI testid="mf-kpi-pending" label="Pending Approvals" value={overview.totals.pending_approvals} icon={UserCheck} color="#ff9800" />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 24 }}>
                <KPI testid="mf-kpi-subs" label="Active Subscriptions" value={overview.totals.active_subscriptions} icon={DollarSign} color="#4caf50" />
                <KPI testid="mf-kpi-mrr" label="Proxy MRR" value={`$${overview.mrr_proxy_usd.toFixed(0)}`} icon={DollarSign} color="#ff6b00" />
                <KPI testid="mf-kpi-clips" label="Clips / Highlights" value={`${overview.totals.clips} / ${overview.totals.highlights}`} icon={Film} color="#ce93d8" />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <div className="glass-card" style={{ padding: 16 }}>
                  <h3 style={{ fontSize: 13, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: 'var(--text-secondary)', marginBottom: 10 }}>Users by Role</h3>
                  {Object.entries(overview.users_by_role).map(([role, n]) => (
                    <div key={role} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                      <span style={{ textTransform: 'capitalize', fontSize: 13 }}>{role.replace("_", " ")}</span>
                      <strong style={{ color: 'var(--cyan)' }}>{n}</strong>
                    </div>
                  ))}
                </div>
                <div className="glass-card" style={{ padding: 16 }}>
                  <h3 style={{ fontSize: 13, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: 'var(--text-secondary)', marginBottom: 10 }}>Recent Sign-ups</h3>
                  {overview.recent_signups.slice(0, 7).map(u => (
                    <div key={u.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid rgba(255,255,255,0.04)', fontSize: 13 }}>
                      <span>{u.name || u.email}</span>
                      <span style={{ color: u.approval_status === "approved" ? '#4caf50' : '#ff9800', fontSize: 11, fontWeight: 600, textTransform: 'uppercase' }}>{u.approval_status}</span>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* ══ LEAGUES ══ */}
      {tab === "leagues" && (
        <DataGrid
          testidPrefix="mf-leagues"
          rows={leagues.map(l => ({
            ...l,
            orgs_count: orgs.filter(o => (o.league_ids || []).includes(l.id)).length,
            teams_count: teams.filter(t => (t.league_ids || []).includes(l.id)).length,
          }))}
          columns={[
            { key: "name", label: "League" },
            { key: "season", label: "Season" },
            { key: "orgs_count", label: "Orgs" },
            { key: "teams_count", label: "Teams" },
            { key: "created_at", label: "Created", render: r => (r.created_at || "").split("T")[0] },
          ]}
        />
      )}

      {/* ══ ORGS ══ */}
      {tab === "orgs" && (
        <DataGrid
          testidPrefix="mf-orgs"
          rows={orgs.map(o => ({
            ...o,
            league_name: leagues.filter(l => (o.league_ids || []).includes(l.id)).map(l => l.name).join(", "),
            teams_count: teams.filter(t => t.org_id === o.id).length,
            members_count: orgCounts(users, o.id),
          }))}
          columns={[
            { key: "name", label: "Organization" },
            { key: "league_name", label: "League" },
            { key: "teams_count", label: "Teams" },
            { key: "members_count", label: "Members (P+C)", accessor: r => users.filter(u => u.org_id === r.id && ["player_parent", "coach"].includes(u.role)).length },
            { key: "created_at", label: "Created", render: r => (r.created_at || "").split("T")[0] },
          ]}
        />
      )}

      {/* ══ TEAMS ══ */}
      {tab === "teams" && (
        <DataGrid
          testidPrefix="mf-teams"
          rows={teams.map(t => ({
            ...t,
            org_name: (orgs.find(o => o.id === t.org_id) || {}).name || "",
            member_count: counts(users, t.id),
          }))}
          columns={[
            { key: "name", label: "Team" },
            { key: "org_name", label: "Organization" },
            { key: "age_group", label: "Age" },
            { key: "member_count", label: "Members", accessor: r => users.filter(u => u.team_id === r.id && ["player_parent", "coach"].includes(u.role)).length },
            { key: "created_at", label: "Created", render: r => (r.created_at || "").split("T")[0] },
          ]}
        />
      )}

      {/* ══ USERS ══ */}
      {tab === "users" && (
        <DataGrid
          testidPrefix="mf-users"
          rows={users}
          columns={[
            { key: "name", label: "Name" },
            { key: "email", label: "Email" },
            { key: "role", label: "Role", render: r => <span style={{ textTransform: 'capitalize' }}>{(r.role || "").replace("_", " ")}</span> },
            { key: "approval_status", label: "Status", render: r => (
              <span style={{
                padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600, textTransform: 'uppercase',
                color: r.approval_status === "approved" ? "#4caf50" : r.approval_status === "pending" ? "#ff9800" : "#f44336",
                background: r.approval_status === "approved" ? "rgba(76,175,80,0.12)" : r.approval_status === "pending" ? "rgba(255,152,0,0.12)" : "rgba(244,67,54,0.12)",
              }}>{r.approval_status}</span>
            ) },
            { key: "subscription_status", label: "Sub", render: r => (
              <span style={{
                padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
                color: r.subscription_status === "active" ? "#4caf50" : "#888",
                background: r.subscription_status === "active" ? "rgba(76,175,80,0.12)" : "rgba(255,255,255,0.04)",
              }}>{r.subscription_status || "inactive"}</span>
            ) },
            { key: "league_name", label: "League" },
            { key: "org_name", label: "Org" },
            { key: "team_name", label: "Team" },
            { key: "created_at", label: "Created", render: r => (r.created_at || "").split("T")[0] },
          ]}
          rowActions={r => (
            <div style={{ display: 'inline-flex', gap: 6, flexWrap: 'wrap' }}>
              <button data-testid={`mf-edit-user-${r.id}`} onClick={() => setEditUser(r)} title="Edit profile" style={userActionStyle('cyan')}>
                Edit
              </button>
              <select
                data-testid={`mf-role-${r.id}`}
                value={r.role || ""}
                disabled={r.id === user.id}
                onChange={e => { if (e.target.value !== r.role) changeUserRole(r, e.target.value); }}
                title="Change role (upgrade / downgrade)"
                style={{
                  padding: '4px 8px', background: '#0f141d',
                  border: '1px solid rgba(156,39,176,0.35)', borderRadius: 8,
                  color: '#ce93d8', fontSize: 11, fontWeight: 700, cursor: r.id === user.id ? 'not-allowed' : 'pointer',
                  fontFamily: "'DM Sans', sans-serif",
                }}
              >
                <option value="ysv_admin">YSV Admin</option>
                <option value="league_admin">League Admin</option>
                <option value="org_admin">Org Admin</option>
                <option value="coach">Coach</option>
                <option value="player_parent">Player/Parent</option>
                <option value="amateur">Amateur</option>
                <option value="gdo">GDO</option>
              </select>
              <button data-testid={`mf-toggle-sub-${r.id}`} onClick={() => toggleSubscription(r)} title="Toggle subscription active/inactive" style={userActionStyle(r.subscription_status === 'active' ? 'green' : 'orange')}>
                {r.subscription_status === 'active' ? 'Sub ✓' : 'Activate'}
              </button>
              <button data-testid={`mf-sync-sub-${r.id}`} onClick={() => syncSubscription(r)} title="Re-sync subscription from Stripe" style={userActionStyle('cyan')}>
                <RotateCcw size={11} />
              </button>
              <button data-testid={`mf-reset-pw-${r.id}`} onClick={() => setResetPwUser(r)} title="Reset password" style={userActionStyle('orange')}>
                <RotateCcw size={11} /> PW
              </button>
              <button data-testid={`mf-delete-user-${r.id}`} onClick={() => deleteUser(r)} disabled={r.id === user.id} title="Delete user" style={{ ...userActionStyle('red'), opacity: r.id === user.id ? 0.4 : 1, cursor: r.id === user.id ? 'not-allowed' : 'pointer' }}>
                <Trash2 size={11} />
              </button>
            </div>
          )}
        />
      )}

      {tab === "approvals" && (
        <div data-testid="mf-approvals">
          <div style={{ marginBottom: 16 }}>
            <h2 style={{ fontSize: 18, fontWeight: 700, fontFamily: "'Outfit', sans-serif" }}>Pending Approvals</h2>
            <p style={{ color: 'var(--text-secondary)', fontSize: 13, marginTop: 4 }}>
              YSV-wide override — shows every user awaiting approval regardless of league/org scope. Approving here bypasses the normal coach/org/league routing.
            </p>
          </div>
          {pendingUsers.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 60, background: '#0c0c10', border: '1px solid var(--dark-border)', borderRadius: 12 }}>
              <UserCheck size={40} style={{ color: 'var(--text-secondary)', opacity: 0.4, marginBottom: 10 }} />
              <p style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-secondary)' }}>No users waiting for approval.</p>
            </div>
          ) : (
            <DataGrid
              testidPrefix="mf-pending"
              rows={pendingUsers}
              columns={[
                { key: "name", label: "Name" },
                { key: "email", label: "Email" },
                { key: "role", label: "Role", render: r => <span style={{ textTransform: 'capitalize' }}>{(r.role || "").replace("_", " ")}</span> },
                { key: "league_name", label: "League" },
                { key: "org_name", label: "Org" },
                { key: "team_name", label: "Team" },
                { key: "parent_email", label: "Parent Email" },
                { key: "created_at", label: "Requested", render: r => (r.created_at || "").split("T")[0] },
              ]}
              rowActions={r => (
                <div style={{ display: 'inline-flex', gap: 6 }}>
                  <button data-testid={`mf-approve-${r.id}`} onClick={() => decideApproval(r, "approve")} style={{ ...userActionStyle('green'), padding: '6px 12px' }}>
                    <CheckCircle2 size={12} /> Approve
                  </button>
                  <button data-testid={`mf-reject-${r.id}`} onClick={() => decideApproval(r, "reject")} style={{ ...userActionStyle('red'), padding: '6px 12px' }}>
                    <X size={12} /> Reject
                  </button>
                </div>
              )}
            />
          )}
        </div>
      )}

      {/* ══ GAMES ══ */}
      {tab === "games" && (
        <DataGrid
          testidPrefix="mf-games"
          rows={games}
          columns={[
            { key: "title", label: "Title" },
            { key: "team_name", label: "Home Team", render: r => r.team_name || <span style={{ color: '#ff9800', fontStyle: 'italic' }}>— (unassigned)</span> },
            { key: "away_team_name", label: "Away Team", render: r => r.away_team_name || (r.opponent ? <span style={{ color: '#888' }}>{r.opponent}</span> : <span style={{ color: '#ff9800', fontStyle: 'italic' }}>—</span>) },
            { key: "org_name", label: "Org" },
            { key: "game_date", label: "Date" },
            { key: "duration_seconds", label: "Len (s)" },
            { key: "upload_status", label: "Status" },
            { key: "uploaded_by_name", label: "Uploaded by" },
          ]}
          rowActions={r => (
            <div style={{ display: 'inline-flex', gap: 6, whiteSpace: 'nowrap' }}>
              <button
                data-testid={`mf-download-game-${r.id}`}
                onClick={() => downloadGame(r)}
                disabled={downloading === r.id}
                title="Download original video"
                style={{
                  padding: '5px 10px', background: 'rgba(0,229,255,0.12)',
                  border: '1px solid rgba(0,229,255,0.35)', borderRadius: 8,
                  color: 'var(--cyan)', fontSize: 11, fontWeight: 700, cursor: 'pointer',
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                  fontFamily: "'DM Sans', sans-serif",
                  opacity: downloading === r.id ? 0.5 : 1,
                }}
              >
                <Download size={11} /> {downloading === r.id ? "…" : "Download"}
              </button>
              <button
                data-testid={`mf-game-menu-${r.id}`}
                onClick={(e) => toggleGameMenu(e, r)}
                title="More actions"
                aria-expanded={gameMenu?.row.id === r.id}
                style={gameMenu?.row.id === r.id ? {
                  // Open state must be unmistakable — the menu hangs off this button.
                  padding: '5px 10px', background: 'rgba(0,229,255,0.2)',
                  border: '1px solid var(--cyan)', borderRadius: 8,
                  color: 'var(--cyan)', fontSize: 11, fontWeight: 700, cursor: 'pointer',
                  fontFamily: "'DM Sans', sans-serif",
                } : {
                  padding: '5px 10px', background: 'rgba(255,255,255,0.06)',
                  border: '1px solid rgba(255,255,255,0.25)', borderRadius: 8,
                  color: '#fff', fontSize: 11, fontWeight: 700, cursor: 'pointer',
                  fontFamily: "'DM Sans', sans-serif",
                }}
              >
                ⋯
              </button>
            </div>
          )}
        />
      )}

      {/* ── Games-grid "⋯" menu — Floating UI anchors it to the button. Rendered
          in a PORTAL to <body>: the page's fade-in animations put transforms on
          ancestors, which silently re-bases position:fixed and offsets the menu
          (the clipping bug). Portals are how dropdown libraries dodge this. ── */}
      {gameMenu && createPortal(
        <>
          <div data-testid="mf-game-menu-overlay" onClick={() => setGameMenu(null)}
               style={{ position: 'fixed', inset: 0, zIndex: 1500 }} />
          <div ref={gameMenuRefs.setFloating} style={{
            ...gameMenuStyles, zoom: 1 / bodyZoom, width: 'max-content', minWidth: 200,
            background: '#15151a', border: '1px solid rgba(0,229,255,0.35)', borderRadius: 10,
            boxShadow: '0 12px 32px rgba(0,0,0,0.6)', zIndex: 1501, overflow: 'hidden',
            fontFamily: "'DM Sans', sans-serif",
          }}>
            <div style={{ zoom: bodyZoom }}>
            {[
              { testid: `mf-rename-game-${gameMenu.row.id}`, icon: Pencil, label: 'Rename game', color: '#fff', run: renameGame },
              { testid: `mf-reassign-home-${gameMenu.row.id}`, icon: Shuffle, label: 'Change home team', color: '#ff6b00', run: (row) => openReassign(row, 'home') },
              { testid: `mf-reassign-away-${gameMenu.row.id}`, icon: Shuffle, label: 'Change away team', color: 'var(--cyan)', run: (row) => openReassign(row, 'away') },
              ...(gameMenu.row.team_id ? [{ testid: `mf-remove-from-portal-${gameMenu.row.id}`, icon: EyeOff, label: 'Remove from home portal', color: '#f44336', run: (row) => removeFromPortal(row, 'home') }] : []),
              ...(gameMenu.row.away_team_id ? [{ testid: `mf-remove-away-from-portal-${gameMenu.row.id}`, icon: EyeOff, label: 'Remove from away portal', color: '#f44336', run: (row) => removeFromPortal(row, 'away') }] : []),
            ].map(item => (
              <button key={item.testid} data-testid={item.testid}
                onClick={() => gameMenuAction(item.run)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8, width: '100%',
                  padding: '9px 12px', background: 'transparent', border: 'none',
                  borderBottom: '1px solid rgba(255,255,255,0.05)', color: item.color,
                  fontSize: 12, fontWeight: 600, cursor: 'pointer', textAlign: 'left',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
              >
                <item.icon size={12} /> {item.label}
              </button>
            ))}
            </div>
          </div>
        </>,
        document.body
      )}

      {/* ══ SCHEDULES ══ */}
      {tab === "schedules" && (
        <DataGrid
          testidPrefix="mf-schedules"
          rows={schedules}
          columns={[
            { key: "game_date", label: "Date" },
            { key: "game_time", label: "Time" },
            { key: "home_team_name", label: "Home" },
            { key: "away_team_name", label: "Away" },
            { key: "location", label: "Location" },
            { key: "age_group", label: "Age" },
            { key: "assigned_gdo_name", label: "GDO" },
            { key: "status", label: "Status" },
          ]}
        />
      )}

      {/* ══ ADVISORY BOARD ══ */}
      {tab === "advisory" && (
        <DataGrid
          testidPrefix="mf-advisory"
          rows={advisoryApps}
          columns={[
            { key: "name", label: "Name", accessor: r => `${r.first_name || ""} ${r.last_name || ""}`.trim(), render: r => <strong>{`${r.first_name || ""} ${r.last_name || ""}`.trim()}</strong> },
            { key: "email", label: "Email" },
            { key: "phone", label: "Phone" },
            { key: "organization", label: "Organization" },
            { key: "support_message", label: "Message", render: r => (
              <span style={{ color: r.support_message ? '#e6e6ea' : '#666', fontStyle: r.support_message ? 'normal' : 'italic' }}>
                {r.support_message ? (r.support_message.length > 80 ? r.support_message.slice(0, 80) + "…" : r.support_message) : "(none)"}
              </span>
            ) },
            { key: "status", label: "Status", render: r => <StatusPill status={r.status || "new"} /> },
            { key: "created_at", label: "Submitted", render: r => (r.created_at || "").split("T")[0] },
          ]}
          rowActions={r => (
            <div style={{ display: 'inline-flex', gap: 6 }}>
              <button data-testid={`mf-advisory-view-${r.id}`} onClick={() => setActiveApp({ kind: "advisory", app: r })} style={appBtn("cyan")}>
                <Eye size={11} /> Open
              </button>
              {(r.status || "new") !== "contacted" && (
                <button data-testid={`mf-advisory-contact-${r.id}`} onClick={() => updateAppStatus("advisory", r.id, "contacted")} style={appBtn("green")}>
                  <CheckCircle2 size={11} /> Contacted
                </button>
              )}
            </div>
          )}
          empty="No Advisory Board applications yet."
        />
      )}

      {/* ══ GDO APPS ══ */}
      {tab === "gdo-apps" && (
        <DataGrid
          testidPrefix="mf-gdo-apps"
          rows={gdoApps}
          columns={[
            { key: "name", label: "Name", accessor: r => `${r.first_name || ""} ${r.last_name || ""}`.trim(), render: r => <strong>{`${r.first_name || ""} ${r.last_name || ""}`.trim()}</strong> },
            { key: "email", label: "Email" },
            { key: "phone", label: "Phone" },
            { key: "regions", label: "Regions", accessor: r => (r.regions || []).join(", "), render: r => (r.regions || []).join(", ") || <span style={{ color: '#666' }}>—</span> },
            { key: "age_range", label: "Age" },
            { key: "status", label: "Status", render: r => <StatusPill status={r.status || "new"} /> },
            { key: "created_at", label: "Submitted", render: r => (r.created_at || "").split("T")[0] },
          ]}
          rowActions={r => (
            <div style={{ display: 'inline-flex', gap: 6 }}>
              <button data-testid={`mf-gdo-apps-view-${r.id}`} onClick={() => setActiveApp({ kind: "gdo-apps", app: r })} style={appBtn("cyan")}>
                <Eye size={11} /> Open
              </button>
              {(r.status || "new") !== "contacted" && (
                <button data-testid={`mf-gdo-apps-contact-${r.id}`} onClick={() => updateAppStatus("gdo-apps", r.id, "contacted")} style={appBtn("green")}>
                  <CheckCircle2 size={11} /> Contacted
                </button>
              )}
            </div>
          )}
          empty="No GDO career applications yet."
        />
      )}

      {/* ══ LEAGUE / ORG SIGNUPS ══ */}
      {tab === "signups" && (
        <DataGrid
          testidPrefix="mf-signups"
          rows={signups}
          columns={[
            { key: "name", label: "Name", accessor: r => `${r.first_name || ""} ${r.last_name || ""}`.trim(), render: r => <strong>{`${r.first_name || ""} ${r.last_name || ""}`.trim()}</strong> },
            { key: "email", label: "Email" },
            { key: "phone", label: "Phone" },
            { key: "area", label: "Area" },
            { key: "signup_type", label: "Type", render: r => (
              <span style={{ textTransform: 'capitalize', color: r.signup_type === "league" ? "#00e5ff" : "#ff6b00", fontWeight: 700 }}>{r.signup_type}</span>
            ) },
            { key: "size", label: "# Orgs/Teams", accessor: r => r.signup_type === "league" ? r.org_count : r.team_count },
            { key: "names", label: "Org/Team Names", accessor: r => (r.signup_type === "league" ? r.org_names : r.team_names || []).join(", "), render: r => {
              const list = r.signup_type === "league" ? (r.org_names || []) : (r.team_names || []);
              const joined = list.join(", ");
              return <span title={joined}>{joined.length > 60 ? joined.slice(0, 60) + "…" : joined}</span>;
            } },
            { key: "status", label: "Status", render: r => <StatusPill status={r.status || "new"} /> },
            { key: "created_at", label: "Submitted", render: r => (r.created_at || "").split("T")[0] },
          ]}
          rowActions={r => (
            <div style={{ display: 'inline-flex', gap: 6 }}>
              <button data-testid={`mf-signups-view-${r.id}`} onClick={() => setActiveApp({ kind: "signups", app: r })} style={appBtn("cyan")}>
                <Eye size={11} /> Open
              </button>
              {(r.status || "new") !== "contacted" && (
                <button data-testid={`mf-signups-contact-${r.id}`} onClick={() => updateAppStatus("signups", r.id, "contacted")} style={appBtn("green")}>
                  <CheckCircle2 size={11} /> Contacted
                </button>
              )}
            </div>
          )}
          empty="No League / Organization signup requests yet."
        />
      )}

      {/* ══ PROMO BANNER ══ */}
      {tab === "promo" && <PromoBannerTab api={api} showFeedback={showFeedback} />}

      {/* ══ WEBHOOKS (Stripe diagnostic) ══ */}
      {tab === "webhooks" && <WebhookEventsTab api={api} showFeedback={showFeedback} />}

      {/* ══ GDO (embed existing panel) ══ */}
      {tab === "gdo" && (
        <div data-testid="mf-gdo-embed">
          <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 14 }}>
            Full GDO management is available on <a href="/admin" style={{ color: 'var(--cyan)', fontWeight: 600 }}>Admin → GDO</a> with per-operator schedule editing. Summary below.
          </p>
          <DataGrid
            testidPrefix="mf-gdo"
            rows={users.filter(u => u.role === "gdo")}
            columns={[
              { key: "name", label: "Operator" },
              { key: "phone", label: "Phone" },
              { key: "email", label: "Email" },
              { key: "assigned_team_ids", label: "Teams", accessor: r => (r.assigned_team_ids || []).length },
              { key: "gdo_schedules", label: "Games Assigned", accessor: r => schedules.filter(s => s.assigned_gdo_id === r.id).length },
              { key: "approval_status", label: "Status" },
              { key: "created_at", label: "Created", render: r => (r.created_at || "").split("T")[0] },
            ]}
          />
        </div>
      )}

      {/* Desktop-only guard banner for small screens */}
      <div style={{ display: 'none' }} className="mf-mobile-banner" />

      {/* Reassign Game modal */}
      {reassignTarget && (
        <div data-testid="mf-reassign-modal" onClick={(e) => { if (e.target === e.currentTarget) closeReassign(); }} style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 2000, padding: 16,
        }}>
          <div className="glass-card" style={{ padding: 0, width: 'min(96vw, 480px)' }}>
            <div style={{ padding: '16px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid var(--dark-border)' }}>
              <div>
                <p style={{ fontSize: 11, color: reassignSide === "home" ? '#ff6b00' : 'var(--cyan)', fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase' }}>
                  {reassignSide === "home" ? "Edit home team" : "Edit away team"}
                </p>
                <h3 style={{ fontSize: 17, fontWeight: 700, fontFamily: "'Outfit', sans-serif", color: '#fff', marginTop: 2 }}>Reassign game</h3>
                <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>"{reassignTarget.title}"</p>
              </div>
              <button data-testid="mf-reassign-close" onClick={closeReassign} style={{ background: 'none', border: 'none', color: '#888', cursor: 'pointer', padding: 4 }}>
                <X size={20} />
              </button>
            </div>

            <div style={{ padding: 20 }}>
              <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 8 }}>
                Currently {reassignSide === "home" ? "home" : "away"} team:{" "}
                <strong style={{ color: '#fff' }}>
                  {reassignSide === "home"
                    ? (reassignTarget.team_name || "(unassigned)")
                    : (reassignTarget.away_team_name || reassignTarget.opponent || "(unassigned)")}
                </strong>
              </p>

              <label style={{ fontSize: 12, color: 'var(--text-secondary)', display: 'block', marginBottom: 6 }}>
                Set {reassignSide} team to
              </label>
              <select
                data-testid="mf-reassign-team"
                value={reassignTeamId}
                onChange={e => setReassignTeamId(e.target.value)}
                style={{
                  width: '100%', padding: '10px 12px',
                  background: '#0c0c10', border: '1px solid var(--dark-border)',
                  borderRadius: 8, color: '#fff', fontSize: 13, fontFamily: "'DM Sans', sans-serif",
                }}
              >
                <option value="">— Remove from any team portal (YSV-only) —</option>
                {[...teams]
                  .filter(t => reassignSide === "home" ? t.id !== reassignTarget.away_team_id : t.id !== reassignTarget.team_id)
                  .map(t => ({ ...t, org_name: (orgs.find(o => o.id === t.org_id) || {}).name || "" }))
                  .sort((a, b) => (a.org_name || "").localeCompare(b.org_name || "") || (a.name || "").localeCompare(b.name || ""))
                  .map(t => (
                    <option key={t.id} value={t.id}>
                      {t.org_name ? `${t.org_name} · ` : ""}{t.name}{t.age_group ? ` (${t.age_group})` : ""}
                    </option>
                  ))}
              </select>

              <p style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 10, lineHeight: 1.5 }}>
                <Eye size={11} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} />
                Members of the new team will receive an in-app notification that a game was added to their portal.
              </p>

              {reassignError && <p data-testid="mf-reassign-error" style={{ color: '#f44336', fontSize: 13, marginTop: 12 }}>{reassignError}</p>}
            </div>

            <div style={{ display: 'flex', gap: 10, padding: '12px 20px', borderTop: '1px solid var(--dark-border)' }}>
              <button onClick={closeReassign} style={{
                flex: 1, padding: 10, background: 'transparent',
                border: '1px solid var(--dark-border)', borderRadius: 10,
                color: '#fff', fontWeight: 600, cursor: 'pointer',
              }}>Cancel</button>
              <button data-testid="mf-reassign-submit" onClick={submitReassign} disabled={reassigning} className="btn-primary" style={{
                flex: 1, padding: 10, opacity: reassigning ? 0.6 : 1,
                cursor: reassigning ? 'not-allowed' : 'pointer',
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              }}>
                <Shuffle size={14} /> {reassigning ? "Saving…" : "Reassign"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Application detail modal */}
      {activeApp && (
        <div data-testid="mf-app-detail-modal" onClick={(e) => { if (e.target === e.currentTarget) setActiveApp(null); }} style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 2000, padding: 16,
        }}>
          <div className="glass-card" style={{ padding: 0, width: 'min(96vw, 560px)', maxHeight: '85vh', display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: '16px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid var(--dark-border)' }}>
              <div>
                <p style={{ fontSize: 11, color: 'var(--cyan)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1 }}>
                  {activeApp.kind === "advisory" ? "Advisory Board" : activeApp.kind === "gdo-apps" ? "GDO Career" : "League / Org Signup"}
                </p>
                <h3 style={{ fontSize: 18, fontWeight: 700, fontFamily: "'Outfit', sans-serif", color: '#fff', marginTop: 2 }}>
                  {`${activeApp.app.first_name || ""} ${activeApp.app.last_name || ""}`.trim() || "(unnamed)"}
                </h3>
                <div style={{ marginTop: 6 }}><StatusPill status={activeApp.app.status || "new"} /></div>
              </div>
              <button data-testid="mf-app-detail-close" onClick={() => setActiveApp(null)} style={{ background: 'none', border: 'none', color: '#888', cursor: 'pointer', padding: 4 }}>
                <X size={20} />
              </button>
            </div>

            <div style={{ padding: 20, overflowY: 'auto' }}>
              <DetailRow icon={Mail} label="Email" value={
                activeApp.app.email
                  ? <a href={`mailto:${activeApp.app.email}`} style={{ color: 'var(--cyan)' }}>{activeApp.app.email}</a>
                  : "—"
              } />
              <DetailRow icon={Phone} label="Phone" value={
                activeApp.app.phone
                  ? <a href={`tel:${activeApp.app.phone}`} style={{ color: 'var(--cyan)' }}>{activeApp.app.phone}</a>
                  : "—"
              } />
              {activeApp.kind === "advisory" && (
                <>
                  <DetailRow icon={Building2} label="Organization" value={activeApp.app.organization || "—"} />
                  <DetailRow icon={Heart} label="How can YSV support youth football?" stack value={activeApp.app.support_message || <em style={{ color: '#888' }}>(no answer)</em>} />
                </>
              )}
              {activeApp.kind === "gdo-apps" && (
                <>
                  <DetailRow icon={Trophy} label="Regions" value={(activeApp.app.regions || []).join(", ") || "—"} />
                  <DetailRow icon={Users} label="Age range" value={activeApp.app.age_range || "—"} />
                  {/* Intro sports questions (pre-screening step 1) */}
                  {activeApp.app.likes_sports && (
                    <DetailRow icon={Trophy} label="Likes / played a sport?" value={
                      <span style={{ textTransform: 'capitalize', fontWeight: 700, color: activeApp.app.likes_sports === "yes" ? "#4caf50" : "#ff9800" }}>
                        {activeApp.app.likes_sports}
                      </span>
                    } />
                  )}
                  {activeApp.app.likes_sports === "no" && activeApp.app.interested_in_videography && (
                    <DetailRow icon={Video} label="Into videography/filming?" value={
                      <span style={{ textTransform: 'capitalize', fontWeight: 700 }}>{activeApp.app.interested_in_videography}</span>
                    } />
                  )}
                  {activeApp.app.favorite_team_or_player && (
                    <DetailRow icon={Heart} label="Favorite team / player" value={activeApp.app.favorite_team_or_player} />
                  )}
                </>
              )}
              {activeApp.kind === "signups" && (
                <>
                  <DetailRow icon={Trophy} label="Area" value={activeApp.app.area || "—"} />
                  <DetailRow icon={Briefcase} label="Type" value={
                    <span style={{ textTransform: 'capitalize', fontWeight: 700, color: activeApp.app.signup_type === "league" ? "#00e5ff" : "#ff6b00" }}>
                      {activeApp.app.signup_type}
                    </span>
                  } />
                  <DetailRow icon={Building2} stack
                    label={activeApp.app.signup_type === "league" ? `Organizations (${activeApp.app.org_count})` : `Teams (${activeApp.app.team_count})`}
                    value={
                      <ul style={{ margin: 0, paddingLeft: 20, color: '#fff' }}>
                        {(activeApp.app.signup_type === "league" ? activeApp.app.org_names : activeApp.app.team_names || []).map((n, i) => (
                          <li key={i} style={{ padding: '2px 0' }}>{n}</li>
                        ))}
                      </ul>
                    }
                  />
                </>
              )}
              <DetailRow icon={Calendar} label="Submitted" value={(activeApp.app.created_at || "").replace("T", " ").split(".")[0]} />
              <DetailRow icon={ClipboardList} label="Application ID" value={<code style={{ fontSize: 11, color: '#888' }}>{activeApp.app.id}</code>} />
            </div>

            <div style={{ display: 'flex', gap: 10, padding: '12px 20px', borderTop: '1px solid var(--dark-border)', flexWrap: 'wrap' }}>
              {activeApp.kind === "gdo-apps" && (activeApp.app.pre_screening && Object.keys(activeApp.app.pre_screening).length > 0
                || activeApp.app.likes_sports || activeApp.app.favorite_team_or_player) && (
                <button
                  data-testid="mf-app-view-prescreening"
                  onClick={() => setPreScreeningView(activeApp.app)}
                  style={{
                    padding: '10px 16px', background: 'rgba(0,229,255,0.10)', border: '1px solid var(--cyan)',
                    borderRadius: 999, color: 'var(--cyan)', fontWeight: 700, cursor: 'pointer',
                    display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12,
                  }}
                ><FileText size={14} /> View Pre-Screening</button>
              )}
              {(activeApp.app.status || "new") === "contacted" ? (
                <button data-testid="mf-app-reopen" onClick={() => updateAppStatus(activeApp.kind, activeApp.app.id, "new")} style={{
                  flex: 1, padding: 10, background: 'transparent', border: '1px solid var(--dark-border)',
                  borderRadius: 10, color: '#fff', fontWeight: 600, cursor: 'pointer',
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                }}><RotateCcw size={14} /> Reopen</button>
              ) : (
                <button data-testid="mf-app-mark-contacted" onClick={() => updateAppStatus(activeApp.kind, activeApp.app.id, "contacted")} style={{
                  flex: 1, padding: 10, background: '#4caf50', border: 'none',
                  borderRadius: 10, color: '#fff', fontWeight: 700, cursor: 'pointer',
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                }}><CheckCircle2 size={14} /> Mark contacted</button>
              )}
              {(activeApp.app.status || "new") !== "dismissed" && (
                <button data-testid="mf-app-dismiss" onClick={() => updateAppStatus(activeApp.kind, activeApp.app.id, "dismissed")} style={{
                  flex: 1, padding: 10, background: 'transparent', border: '1px solid rgba(244,67,54,0.4)',
                  borderRadius: 10, color: '#f44336', fontWeight: 600, cursor: 'pointer',
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                }}><Trash2 size={14} /> Dismiss</button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Pre-screening viewer modal (GDO applications) */}
      {preScreeningView && (
        <PreScreeningViewer app={preScreeningView} onClose={() => setPreScreeningView(null)} />
      )}

      {/* Edit user modal */}
      {editUser && (
        <EditUserModal
          api={api}
          target={editUser}
          orgs={orgs}
          teams={teams}
          leagues={leagues}
          onClose={() => setEditUser(null)}
          onSaved={() => { setEditUser(null); refreshUsers(); }}
        />
      )}

      {/* Reset password modal */}
      {resetPwUser && (
        <ResetPasswordModal
          api={api}
          target={resetPwUser}
          onClose={() => setResetPwUser(null)}
          onSaved={() => setResetPwUser(null)}
        />
      )}

      {/* PWA-safe confirmation dialog (replaces window.confirm which is broken in some iOS PWA contexts) */}
      {confirmAction && (
        <ConfirmActionModal
          action={confirmAction}
          onClose={() => setConfirmAction(null)}
        />
      )}

      {/* Action feedback toast — replaces alert(), iOS-PWA-safe */}
      {actionFeedback && (
        <ActionFeedbackToast feedback={actionFeedback} onClose={() => setActionFeedback(null)} />
      )}

      <style>{`
        @media (max-width: 900px) {
          [data-testid="mainframe-page"] { padding: 20px 12px; }
        }
      `}</style>
    </div>
  );
};

/* ═══ Small shared action button styling helper ═══ */
const userActionStyle = (tone) => {
  const tones = {
    cyan: { color: 'var(--cyan)', bg: 'rgba(0,229,255,0.1)', bd: 'rgba(0,229,255,0.35)' },
    orange: { color: '#ff9800', bg: 'rgba(255,152,0,0.1)', bd: 'rgba(255,152,0,0.35)' },
    green: { color: '#4caf50', bg: 'rgba(76,175,80,0.12)', bd: 'rgba(76,175,80,0.35)' },
    red: { color: '#f44336', bg: 'rgba(244,67,54,0.1)', bd: 'rgba(244,67,54,0.35)' },
  };
  const t = tones[tone] || tones.cyan;
  return {
    padding: '4px 10px', borderRadius: 6,
    background: t.bg, border: `1px solid ${t.bd}`, color: t.color,
    fontSize: 11, fontWeight: 700, cursor: 'pointer',
    display: 'inline-flex', alignItems: 'center', gap: 4,
    fontFamily: "'DM Sans', sans-serif",
  };
};

/* ═══ Edit user modal ═══ */
const EditUserModal = ({ api, target, orgs, teams, leagues, onClose, onSaved }) => {
  const [form, setForm] = useState({
    name: target.name || "",
    email: target.email || "",
    phone: target.phone || "",
    league_id: target.league_id || "",
    org_id: target.org_id || "",
    team_id: target.team_id || "",
    jersey_number: target.jersey_number || "",
    player_dob: target.player_dob || "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Clubs aren't league-scoped post-remodel, so don't filter the org picker by the
  // chosen league — show all clubs (the user's league/org/team are still set below).
  const orgOptions = orgs;
  const teamsInOrg = form.org_id ? teams.filter(t => t.org_id === form.org_id) : teams;

  const save = async () => {
    setSaving(true); setError(null);
    try {
      // Update profile fields (name/email/phone/jersey/dob)
      await api().put(`/users/${target.id}`, {
        name: form.name, email: form.email, phone: form.phone,
        jersey_number: form.jersey_number, player_dob: form.player_dob,
      });
      // Update scope (league/org/team) via a second call — separate endpoint handles hierarchy
      const scopeChanged =
        form.league_id !== (target.league_id || "") ||
        form.org_id !== (target.org_id || "") ||
        form.team_id !== (target.team_id || "");
      if (scopeChanged) {
        await api().put(`/users/${target.id}`, {
          league_id: form.league_id || null,
          org_id: form.org_id || null,
          team_id: form.team_id || null,
        });
      }
      onSaved();
    } catch (e) {
      setError(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div data-testid="mf-edit-user-modal" onClick={(e) => { if (e.target === e.currentTarget && !saving) onClose(); }} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000, padding: 16,
    }}>
      <div className="glass-card" style={{ padding: 0, width: 'min(96vw, 560px)', maxHeight: '88vh', overflowY: 'auto' }}>
        <div style={{ padding: '16px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid var(--dark-border)' }}>
          <div>
            <p style={{ fontSize: 11, color: 'var(--cyan)', fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase' }}>Edit user</p>
            <h3 style={{ fontSize: 17, fontWeight: 700, fontFamily: "'Outfit', sans-serif", marginTop: 2 }}>{target.name || target.email}</h3>
            <p style={{ fontSize: 12, color: 'var(--text-secondary)', textTransform: 'capitalize' }}>{(target.role || "").replace("_", " ")}</p>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#888', cursor: 'pointer', padding: 4 }}>
            <X size={20} />
          </button>
        </div>
        <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <label style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Name
            <input data-testid="mf-edit-name" className="input-field" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
          </label>
          <label style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Email
            <input data-testid="mf-edit-email" className="input-field" type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
          </label>
          <label style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Phone
            <input data-testid="mf-edit-phone" className="input-field" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} />
          </label>
          {target.role === "player_parent" && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <label style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Jersey #
                <input data-testid="mf-edit-jersey" className="input-field" value={form.jersey_number} onChange={e => setForm({ ...form, jersey_number: e.target.value })} />
              </label>
              <label style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Player DOB
                <input data-testid="mf-edit-dob" className="input-field" type="date" value={form.player_dob ? form.player_dob.split("T")[0] : ""} onChange={e => setForm({ ...form, player_dob: e.target.value })} />
              </label>
            </div>
          )}
          <div style={{ borderTop: '1px solid var(--dark-border)', paddingTop: 12, marginTop: 4 }}>
            <p style={{ fontSize: 11, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>Affiliation</p>
            <label style={{ fontSize: 12, color: 'var(--text-secondary)' }}>League
              <select data-testid="mf-edit-league" className="input-field" value={form.league_id} onChange={e => setForm({ ...form, league_id: e.target.value, org_id: "", team_id: "" })}>
                <option value="">— None —</option>
                {leagues.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
            </label>
            <label style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 10, display: 'block' }}>Organization
              <select data-testid="mf-edit-org" className="input-field" value={form.org_id} onChange={e => setForm({ ...form, org_id: e.target.value, team_id: "" })}>
                <option value="">— None —</option>
                {orgOptions.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
              </select>
            </label>
            <label style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 10, display: 'block' }}>Team
              <select data-testid="mf-edit-team" className="input-field" value={form.team_id} onChange={e => setForm({ ...form, team_id: e.target.value })}>
                <option value="">— None —</option>
                {teamsInOrg.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </label>
          </div>
          {error && <p data-testid="mf-edit-error" style={{ color: '#f44336', fontSize: 13 }}>{error}</p>}
        </div>
        <div style={{ display: 'flex', gap: 10, padding: '12px 20px', borderTop: '1px solid var(--dark-border)' }}>
          <button onClick={onClose} disabled={saving} style={{ flex: 1, padding: 10, background: 'transparent', border: '1px solid var(--dark-border)', borderRadius: 10, color: '#fff', fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
          <button data-testid="mf-edit-save" onClick={save} disabled={saving} className="btn-primary" style={{ flex: 1, opacity: saving ? 0.6 : 1 }}>
            {saving ? "Saving…" : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ═══ Reset password modal ═══ */
const ResetPasswordModal = ({ api, target, onClose, onSaved }) => {
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);

  const save = async () => {
    setError(null);
    if (pw.length < 6) { setError("Password must be at least 6 characters"); return; }
    if (pw !== confirm) { setError("Passwords don't match"); return; }
    setSaving(true);
    try {
      await api().post(`/mainframe/users/${target.id}/reset-password`, { new_password: pw });
      setDone(true);
      setTimeout(() => onSaved(), 1500);
    } catch (e) {
      setError(e.response?.data?.detail || "Reset failed");
    } finally { setSaving(false); }
  };

  return (
    <div data-testid="mf-reset-pw-modal" onClick={(e) => { if (e.target === e.currentTarget && !saving) onClose(); }} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000, padding: 16,
    }}>
      <div className="glass-card" style={{ padding: 24, width: 'min(96vw, 440px)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
          <div>
            <p style={{ fontSize: 11, color: '#ff9800', fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase' }}>Reset password</p>
            <h3 style={{ fontSize: 16, fontWeight: 700, fontFamily: "'Outfit', sans-serif", marginTop: 2 }}>{target.name || target.email}</h3>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#888', cursor: 'pointer', padding: 4 }}><X size={20} /></button>
        </div>
        {done ? (
          <div style={{ padding: 20, textAlign: 'center' }}>
            <CheckCircle2 size={40} style={{ color: '#4caf50', marginBottom: 10 }} />
            <p style={{ fontSize: 14, fontWeight: 700 }}>Password reset.</p>
            <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>Active sessions were invalidated — the user will need to log in again.</p>
          </div>
        ) : (
          <>
            <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 12 }}>Use this when a user can't receive the forgot-password email. They'll be force-logged-out of active sessions.</p>
            <input data-testid="mf-reset-new" className="input-field" type="password" placeholder="New password (min 6 chars)" value={pw} onChange={e => setPw(e.target.value)} minLength={6} style={{ marginBottom: 10 }} />
            <input data-testid="mf-reset-confirm" className="input-field" type="password" placeholder="Confirm new password" value={confirm} onChange={e => setConfirm(e.target.value)} minLength={6} />
            {error && <p data-testid="mf-reset-error" style={{ color: '#f44336', fontSize: 13, marginTop: 10 }}>{error}</p>}
            <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
              <button onClick={onClose} disabled={saving} style={{ flex: 1, padding: 10, background: 'transparent', border: '1px solid var(--dark-border)', borderRadius: 10, color: '#fff', fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
              <button data-testid="mf-reset-save" onClick={save} disabled={saving} className="btn-primary" style={{ flex: 1, opacity: saving ? 0.6 : 1 }}>
                {saving ? "Saving…" : "Reset Password"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default MainframePage;

/* ═══ GDO Application Pre-Screening Viewer ═══
 * Renders every question + answer from the applicant's Step 2 pre-screening
 * form in a printable modal. Grouped by section headings (same section keys
 * as CareersPage's PRE_SCREENING_SECTIONS) so reviewers can scan quickly
 * during callback triage. Provides a copy-to-clipboard action for pasting
 * into hiring notes and a native window.print() shortcut so YSV can archive
 * a PDF-style record of any application on demand.
 * ─────────────────────────────────────────────────────────────────────── */

// Human-readable question labels for each pre_screening key. Any keys that
// don't have an entry here will fall back to the raw key rendered verbatim
// (safe forward-compat if we add new questions in the future).
const PS_LABELS = {
  saturdays_sundays: "Available Saturdays and/or Sundays during football season (Aug–Nov)?",
  weekends_per_month: "How many weekends per month available?",
  holidays: "Available to work holidays if games are scheduled?",
  reliable_transport: "Has reliable transportation?",
  rideshare_ok: "Comfortable using Uber / rideshare if needed?",
  travel_distance: "Willing to travel how far?",
  lift_30_40: "Can lift and carry 30–40 lbs?",
  outdoor_3_7hr: "Comfortable outdoors 3–7 hours in various weather?",
  weather_ok: "Comfortable in hot summer / cold fall temperatures?",
  tech_comfortable: "Comfortable with smartphones, tablets, computers, camera gear?",
  camera_experience: "Prior camera / sports-recording experience?",
  camera_experience_details: "Describes prior camera experience",
  customer_service_ok: "Comfortable interacting with coaches / parents / athletes / staff?",
  troubleshoot_ok: "Comfortable troubleshooting with support team's help?",
  legally_authorized: "Legally authorized to work in the US?",
  felony_conviction: "Prior felony conviction affecting youth-sports work?",
  why_ysv: "Why interested in becoming a YSV Game Day Operator?",
  ack_weekend: "Ack — Weekend availability required",
  ack_equipment: "Ack — Responsible for YSV camera equipment",
  ack_professionalism: "Ack — Punctuality + professionalism expected",
  ack_outdoors: "Ack — May work outdoors for extended periods",
  ack_certify: "Ack — Information provided is accurate",
};
// `key` here matches PRE_SCREENING_SECTIONS[].key in CareersPage.jsx so the
// `mf-prescreening-section-<slug>` testid lines up with `ps-section-<slug>`.
const PS_SECTION_ORDER = [
  { key: "availability", title: "Availability", keys: ["saturdays_sundays", "weekends_per_month", "holidays"] },
  { key: "transportation", title: "Transportation", keys: ["reliable_transport", "rideshare_ok", "travel_distance"] },
  { key: "physical", title: "Physical Requirements", keys: ["lift_30_40", "outdoor_3_7hr", "weather_ok"] },
  { key: "technology", title: "Technology Experience", keys: ["tech_comfortable", "camera_experience", "camera_experience_details"] },
  { key: "customer_service", title: "Customer Service", keys: ["customer_service_ok", "troubleshoot_ok"] },
  { key: "employment", title: "Employment", keys: ["legally_authorized", "felony_conviction"] },
  { key: "why_ysv", title: "Why YSV?", keys: ["why_ysv"] },
  { key: "acknowledgement", title: "Final Acknowledgement", keys: ["ack_weekend", "ack_equipment", "ack_professionalism", "ack_outdoors", "ack_certify"] },
];

const formatPSAnswer = (v) => {
  if (v === true) return "✓ Acknowledged";
  if (v === false) return "— Not acknowledged";
  if (v === "yes") return "Yes";
  if (v === "no") return "No";
  if (v === "na") return "N/A";
  if (v === undefined || v === null || v === "") return "—";
  return String(v);
};

const PreScreeningViewer = ({ app, onClose }) => {
  const ps = app.pre_screening || {};
  const applicantName = `${app.first_name || ""} ${app.last_name || ""}`.trim() || "GDO applicant";
  const hasAnyData = Object.keys(ps).length > 0 || !!app.likes_sports || !!app.favorite_team_or_player;

  const copyToClipboard = async () => {
    const lines = [];
    lines.push(`GDO Pre-Screening — ${applicantName}`);
    lines.push(`Email: ${app.email || "—"}   Phone: ${app.phone || "—"}`);
    lines.push(`Submitted: ${(app.created_at || "").replace("T", " ").split(".")[0]}`);
    if (app.likes_sports) lines.push(`Likes/played sports: ${app.likes_sports}`);
    if (app.interested_in_videography) lines.push(`Interested in videography: ${app.interested_in_videography}`);
    if (app.favorite_team_or_player) lines.push(`Favorite team/player: ${app.favorite_team_or_player}`);
    lines.push("");
    PS_SECTION_ORDER.forEach(sec => {
      lines.push(`── ${sec.title} ──`);
      sec.keys.forEach(k => {
        if (k in ps) lines.push(`${PS_LABELS[k] || k}: ${formatPSAnswer(ps[k])}`);
      });
      lines.push("");
    });
    try { await navigator.clipboard.writeText(lines.join("\n")); } catch { /* ignore */ }
  };

  return (
    <div
      data-testid="mf-prescreening-viewer"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 2000,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
      }}
    >
      <div className="glass-card" style={{
        padding: 0, width: 'min(96vw, 780px)', maxHeight: '92vh',
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
      }}>
        {/* Header */}
        <div style={{
          padding: '18px 24px', borderBottom: '1px solid var(--dark-border)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
        }}>
          <div>
            <p style={{ fontSize: 10, color: 'var(--cyan)', textTransform: 'uppercase', letterSpacing: 1.5, fontWeight: 700 }}>Pre-Screening Answers</p>
            <h3 style={{ fontSize: 18, fontWeight: 800, color: '#fff', fontFamily: "'Outfit', sans-serif", marginTop: 2 }}>{applicantName}</h3>
            <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>
              {app.email || "—"} · {app.phone || "—"} · Submitted {(app.created_at || "").replace("T", " ").split(".")[0]}
            </p>
          </div>
          <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
            <button data-testid="mf-prescreening-copy" onClick={copyToClipboard} title="Copy to clipboard" style={ghostBtn}>
              <Copy size={14} /> Copy
            </button>
            <button data-testid="mf-prescreening-print" onClick={() => window.print()} title="Print / Save as PDF" style={ghostBtn}>
              <FileText size={14} /> Print
            </button>
            <button data-testid="mf-prescreening-close" onClick={onClose} style={{
              ...ghostBtn, background: 'transparent', border: '1px solid var(--dark-border)',
            }}><X size={14} /></button>
          </div>
        </div>

        {/* Body */}
        <div style={{ overflowY: 'auto', padding: '18px 24px', flex: 1 }}>
          {/* Intro sports questions from Step 1 */}
          {(app.likes_sports || app.favorite_team_or_player) && (
            <div style={{ marginBottom: 20, paddingBottom: 14, borderBottom: '1px dashed var(--dark-border)' }}>
              <p style={{ fontSize: 10, color: '#ff9800', fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase', marginBottom: 10 }}>Intro</p>
              {app.likes_sports && <PSRow label="Likes / played a sport?" value={formatPSAnswer(app.likes_sports)} />}
              {app.likes_sports === "no" && app.interested_in_videography && <PSRow label="Interested in videography/filming?" value={formatPSAnswer(app.interested_in_videography)} />}
              {app.favorite_team_or_player && <PSRow label="Favorite team / player" value={app.favorite_team_or_player} />}
            </div>
          )}

          {!hasAnyData ? (
            <p data-testid="mf-prescreening-empty" style={{ color: 'var(--text-secondary)', fontSize: 14, textAlign: 'center', padding: 24, fontStyle: 'italic' }}>
              This applicant hasn't completed the pre-screening section.
            </p>
          ) : Object.keys(ps).length === 0 ? (
            <p data-testid="mf-prescreening-empty" style={{ color: 'var(--text-secondary)', fontSize: 14, textAlign: 'center', padding: 24, fontStyle: 'italic' }}>
              No sectioned pre-screening answers were submitted.
            </p>
          ) : (
            PS_SECTION_ORDER.map(sec => {
              const rows = sec.keys.filter(k => k in ps);
              if (rows.length === 0) return null;
              return (
                <div key={sec.key} data-testid={`mf-prescreening-section-${sec.key}`} style={{ marginBottom: 20 }}>
                  <p style={{ fontSize: 10, color: 'var(--cyan)', fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase', marginBottom: 10 }}>{sec.title}</p>
                  {rows.map(k => <PSRow key={k} label={PS_LABELS[k] || k} value={formatPSAnswer(ps[k])} />)}
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

const ghostBtn = {
  padding: '7px 12px', background: 'rgba(0,229,255,0.10)', border: '1px solid rgba(0,229,255,0.35)',
  borderRadius: 8, color: 'var(--cyan)', fontWeight: 600, cursor: 'pointer',
  display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12,
  fontFamily: "'DM Sans', sans-serif",
};

const PSRow = ({ label, value }) => (
  <div style={{
    display: 'flex', gap: 12, padding: '8px 0',
    borderBottom: '1px solid rgba(255,255,255,0.05)',
    fontSize: 13, alignItems: 'flex-start',
  }}>
    <div style={{ flex: '0 0 55%', color: 'var(--text-secondary)', lineHeight: 1.5 }}>{label}</div>
    <div style={{ flex: 1, color: '#fff', fontWeight: 500, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{value}</div>
  </div>
);

/* ═══ Inline mini bar chart for the promo banner 7-day stats ═══ */
const Last7DayBars = ({ data }) => {
  if (!data || data.length === 0) return <p style={{ color: 'var(--text-secondary)', fontSize: 12 }}>No data yet.</p>;
  const maxVal = Math.max(1, ...data.flatMap(d => [d.views || 0, d.clicks || 0]));
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 8, alignItems: 'end' }}>
      {data.map((d, i) => {
        const vH = Math.max(2, ((d.views || 0) / maxVal) * 90);
        const cH = Math.max(2, ((d.clicks || 0) / maxVal) * 90);
        const dayLabel = d.day.slice(5).replace("-", "/"); // MM/DD
        return (
          <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 3, height: 96 }}>
              <div title={`${d.views} views`} style={{
                width: 12, height: vH, background: 'linear-gradient(180deg, #00e5ff 0%, #0096a8 100%)',
                borderRadius: '4px 4px 0 0',
              }} />
              <div title={`${d.clicks} clicks`} style={{
                width: 12, height: cH, background: 'linear-gradient(180deg, #ff9800 0%, #c75e00 100%)',
                borderRadius: '4px 4px 0 0',
              }} />
            </div>
            <p style={{ fontSize: 10, color: 'var(--text-secondary)' }}>{dayLabel}</p>
          </div>
        );
      })}
      <div style={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'center', gap: 16, marginTop: 6, fontSize: 11, color: 'var(--text-secondary)' }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          <span style={{ width: 10, height: 10, background: '#00e5ff', borderRadius: 2, display: 'inline-block' }} /> Views
        </span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          <span style={{ width: 10, height: 10, background: '#ff9800', borderRadius: 2, display: 'inline-block' }} /> Clicks
        </span>
      </div>
    </div>
  );
};

/* ═══ Promo Banner manager — single global image for highlight share pages ═══ */
const PromoBannerTab = ({ api, showFeedback }) => {
  const [banner, setBanner] = React.useState(null);
  const [stats, setStats] = React.useState(null);
  const [uploading, setUploading] = React.useState(false);
  const fileRef = React.useRef(null);

  const load = React.useCallback(() => {
    api().get("/mainframe/promo-banner").then(r => setBanner(r.data)).catch(() => setBanner(null));
    api().get("/mainframe/promo-banner/stats").then(r => setStats(r.data)).catch(() => setStats(null));
  }, [api]);

  React.useEffect(() => { load(); }, [load]);

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { showFeedback("Pick an image file", "error"); return; }
    if (file.size > 5 * 1024 * 1024) { showFeedback("Image must be under 5 MB", "error"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api().post("/mainframe/promo-banner", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      showFeedback("Promo banner uploaded — share pages updated", "success");
      load();
    } catch (e) {
      showFeedback("Upload failed: " + (e.response?.data?.detail || e.message), "error");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removeBanner = async () => {
    if (!window.confirm("Remove the promo banner from all highlight share pages?")) return;
    try {
      await api().delete("/mainframe/promo-banner");
      showFeedback("Promo banner removed", "warn");
      load();
    } catch (e) {
      showFeedback("Remove failed: " + (e.response?.data?.detail || e.message), "error");
    }
  };

  return (
    <div data-testid="mf-promo-tab">
      <h2 style={{ fontSize: 18, fontWeight: 800, fontFamily: "'Outfit', sans-serif", marginBottom: 6 }}>Highlight Share Banner</h2>
      <p style={{ color: 'var(--text-secondary)', fontSize: 13, marginBottom: 18, maxWidth: 640 }}>
        Upload a square image (~600×600 px) shown at the bottom of every public highlight share page. Perfect for event promos, sponsor logos, or seasonal CTAs. One global banner — uploading a new one replaces the old.
      </p>

      {/* ═══ Stats bar — visible engagement on the current + lifetime banner ═══ */}
      {stats && (
        <div data-testid="mf-promo-stats" style={{ marginBottom: 24, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10, maxWidth: 880 }}>
          {[
            { label: "Views (current banner)", value: stats.current?.views ?? 0, color: "#00e5ff" },
            { label: "Clicks (current banner)", value: stats.current?.clicks ?? 0, color: "#ff9800" },
            { label: "CTR · current", value: `${stats.current?.ctr_pct ?? 0}%`, color: "#4caf50" },
            { label: "Lifetime views", value: stats.lifetime?.views ?? 0, color: "#888" },
            { label: "Lifetime clicks", value: stats.lifetime?.clicks ?? 0, color: "#888" },
            { label: "Lifetime CTR", value: `${stats.lifetime?.ctr_pct ?? 0}%`, color: "#888" },
          ].map((s, i) => (
            <div key={i} data-testid={`mf-promo-stat-${i}`} className="glass-card" style={{ padding: 14 }}>
              <p style={{ fontSize: 10, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700, marginBottom: 6 }}>{s.label}</p>
              <p style={{ fontSize: 24, fontWeight: 900, fontFamily: "'Outfit', sans-serif", color: s.color, lineHeight: 1 }}>{s.value.toLocaleString ? s.value.toLocaleString() : s.value}</p>
            </div>
          ))}
        </div>
      )}

      {/* Last-7-day mini chart */}
      {stats && stats.last_7_days && (
        <div data-testid="mf-promo-7day-chart" className="glass-card" style={{ padding: 16, marginBottom: 24, maxWidth: 880 }}>
          <p style={{ fontSize: 11, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700, marginBottom: 12 }}>Last 7 days</p>
          <Last7DayBars data={stats.last_7_days} />
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 20, maxWidth: 880 }}>
        {/* Preview */}
        <div className="glass-card" style={{ padding: 18 }}>
          <p style={{ fontSize: 11, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700, marginBottom: 12 }}>Live preview</p>
          {banner?.promo_banner_url ? (
            <>
              <div style={{ aspectRatio: '1 / 1', borderRadius: 12, overflow: 'hidden', background: '#000', marginBottom: 12, border: '1px solid var(--dark-border)' }}>
                <img data-testid="mf-promo-current" src={banner.promo_banner_url} alt="Current promo banner" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              </div>
              <p style={{ fontSize: 11, color: '#888', marginBottom: 12 }}>
                Uploaded: {banner.uploaded_at ? new Date(banner.uploaded_at).toLocaleString() : "—"}
              </p>
              <button data-testid="mf-promo-remove" onClick={removeBanner} style={{
                width: '100%', padding: 10, background: 'rgba(244,67,54,0.1)',
                border: '1px solid rgba(244,67,54,0.35)', borderRadius: 8,
                color: '#f44336', fontSize: 13, fontWeight: 700, cursor: 'pointer',
                fontFamily: "'DM Sans', sans-serif",
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              }}>
                <Trash2 size={13} /> Remove banner
              </button>
            </>
          ) : (
            <div style={{
              aspectRatio: '1 / 1', borderRadius: 12, background: 'rgba(255,255,255,0.02)',
              border: '1px dashed var(--dark-border)', display: 'flex',
              flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8, color: '#666',
            }}>
              <ImageIcon size={32} />
              <p style={{ fontSize: 12 }}>No banner uploaded</p>
            </div>
          )}
        </div>

        {/* Upload */}
        <div className="glass-card" style={{ padding: 18 }}>
          <p style={{ fontSize: 11, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700, marginBottom: 12 }}>{banner?.promo_banner_url ? "Replace banner" : "Upload banner"}</p>
          <input
            ref={fileRef}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            onChange={e => handleFile(e.target.files?.[0])}
            disabled={uploading}
            data-testid="mf-promo-file-input"
            style={{ display: 'none' }}
          />
          <button
            data-testid="mf-promo-upload-btn"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            style={{
              width: '100%', padding: '14px 16px',
              background: uploading ? 'rgba(0,229,255,0.15)' : 'linear-gradient(135deg, #00e5ff 0%, #0aa6c4 100%)',
              border: 'none', borderRadius: 10, color: '#000',
              fontSize: 14, fontWeight: 800, cursor: uploading ? 'default' : 'pointer',
              fontFamily: "'Outfit', sans-serif",
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              opacity: uploading ? 0.7 : 1,
            }}
          >
            <UploadIcon size={16} /> {uploading ? "Uploading…" : (banner?.promo_banner_url ? "Choose new image" : "Choose image to upload")}
          </button>
          <p style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 12, lineHeight: 1.5 }}>
            Recommended: <strong style={{ color: '#fff' }}>600 × 600 px</strong> · PNG, JPG, or WebP · Max 5 MB. Image is uploaded to Cloudflare R2 and served instantly. Changes are visible on every share page within seconds.
          </p>
        </div>
      </div>
    </div>
  );
};

/* ═══ PWA-safe confirmation modal (replaces window.confirm which fails on installed iOS PWA) ═══ */
const ConfirmActionModal = ({ action, onClose }) => {
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState(null);
  const tones = {
    cyan: { color: 'var(--cyan)', bg: 'rgba(0,229,255,0.12)', bd: 'rgba(0,229,255,0.4)' },
    green: { color: '#4caf50', bg: 'rgba(76,175,80,0.18)', bd: 'rgba(76,175,80,0.5)' },
    orange: { color: '#ff9800', bg: 'rgba(255,152,0,0.18)', bd: 'rgba(255,152,0,0.5)' },
    red: { color: '#f44336', bg: 'rgba(244,67,54,0.18)', bd: 'rgba(244,67,54,0.5)' },
  };
  const t = tones[action.tone] || tones.cyan;
  const onYes = async () => {
    setBusy(true);
    setError(null);
    try {
      await action.onConfirm();
      onClose();
    } catch (e) {
      setError(e.response?.data?.detail || e.message || "Action failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div data-testid="mf-confirm-modal" onClick={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 4000, padding: 16, backdropFilter: 'blur(8px)',
    }}>
      <div className="glass-card" style={{ padding: 24, width: 'min(96vw, 480px)' }}>
        <h3 style={{ fontSize: 18, fontWeight: 800, fontFamily: "'Outfit', sans-serif", marginBottom: 8, color: t.color }}>{action.title}</h3>
        <p style={{ fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.55, marginBottom: 16 }}>{action.message}</p>
        {error && <p data-testid="mf-confirm-error" style={{ color: '#f44336', fontSize: 13, marginBottom: 12 }}>{error}</p>}
        <div style={{ display: 'flex', gap: 10 }}>
          <button data-testid="mf-confirm-cancel" onClick={onClose} disabled={busy} style={{
            flex: 1, padding: 12, background: 'transparent',
            border: '1px solid var(--dark-border)', borderRadius: 10,
            color: '#fff', fontWeight: 600, cursor: 'pointer',
            fontFamily: "'DM Sans', sans-serif",
          }}>Cancel</button>
          <button data-testid="mf-confirm-yes" onClick={onYes} disabled={busy} style={{
            flex: 1.4, padding: 12,
            background: t.bg, border: `1px solid ${t.bd}`, borderRadius: 10,
            color: t.color, fontWeight: 800, cursor: busy ? 'default' : 'pointer',
            fontFamily: "'DM Sans', sans-serif",
            opacity: busy ? 0.6 : 1,
          }}>{busy ? (action.busyLabel || "Working…") : "Confirm"}</button>
        </div>
      </div>
    </div>
  );
};

/* ═══ Toast for short feedback (replaces alert()) ═══ */
const ActionFeedbackToast = ({ feedback, onClose }) => {
  const tones = {
    success: { color: '#4caf50', bg: 'rgba(76,175,80,0.95)', bd: 'rgba(76,175,80,0.6)' },
    info: { color: 'var(--cyan)', bg: 'rgba(0,229,255,0.95)', bd: 'rgba(0,229,255,0.6)' },
    warn: { color: '#000', bg: 'rgba(255,152,0,0.97)', bd: 'rgba(255,152,0,0.6)' },
    error: { color: '#fff', bg: 'rgba(244,67,54,0.97)', bd: 'rgba(244,67,54,0.6)' },
  };
  const t = tones[feedback.tone] || tones.info;
  return (
    <div data-testid="mf-feedback-toast" onClick={onClose} style={{
      position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)',
      maxWidth: 'min(92vw, 520px)', padding: '14px 20px',
      background: t.bg, color: t.color,
      border: `1px solid ${t.bd}`, borderRadius: 12,
      zIndex: 5000, boxShadow: '0 10px 40px rgba(0,0,0,0.5)',
      fontSize: 14, fontWeight: 700, fontFamily: "'DM Sans', sans-serif",
      cursor: 'pointer',
      animation: 'mf-toast-rise 0.25s ease-out',
    }}>
      {feedback.message}
      <style>{`
        @keyframes mf-toast-rise {
          from { opacity: 0; transform: translateX(-50%) translateY(20px); }
          to { opacity: 1; transform: translateX(-50%) translateY(0); }
        }
      `}</style>
    </div>
  );
};


/* ─── Webhook Events Tab (Stripe diagnostic) ─── */
const WebhookEventsTab = ({ api, showFeedback }) => {
  const [events, setEvents] = useState([]);
  const [diag, setDiag] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(null); // event id
  const [replaying, setReplaying] = useState(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const [evRes, diagRes] = await Promise.all([
        api().get("/mainframe/webhook-events?limit=100"),
        api().get("/mainframe/webhook-events/diagnostics"),
      ]);
      setEvents(evRes.data || []);
      setDiag(diagRes.data || null);
    } catch (e) {
      showFeedback("Failed to load webhook events: " + (e.response?.data?.detail || e.message), "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const replay = async (ev) => {
    setReplaying(ev.id);
    try {
      const r = await api().post(`/mainframe/webhook-events/${ev.id}/replay`);
      const outcome = r.data?.replay?.outcome;
      const action = r.data?.replay?.action_taken;
      if (outcome === "success") {
        showFeedback(`Replayed ${ev.event_type} → ${action}`, "success");
      } else {
        showFeedback(`Replay finished — outcome: ${outcome}`, "warn");
      }
      await refresh();
    } catch (e) {
      showFeedback("Replay failed: " + (e.response?.data?.detail || e.message), "error");
    } finally {
      setReplaying(null);
    }
  };

  const outcomeColor = (o) => {
    if (o === "success" || o === "safety_net_success") return "#4caf50";
    if (o === "ignored") return "#888";
    if (o === "pending") return "#ff9800";
    return "#ff4444";
  };

  return (
    <div data-testid="mf-webhooks-tab">
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 16 }}>
        <h2 style={{ fontSize: 18, fontWeight: 700, color: '#fff', margin: 0 }}>Stripe Webhook Events</h2>
        <button
          data-testid="mf-webhook-refresh"
          onClick={refresh}
          disabled={loading}
          style={{
            padding: '6px 14px', borderRadius: 8, border: '1px solid var(--cyan)',
            background: 'rgba(0,229,255,0.1)', color: 'var(--cyan)', fontWeight: 600, fontSize: 12,
            cursor: loading ? 'wait' : 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6,
          }}>
          <RefreshCw size={12} /> {loading ? 'Loading…' : 'Refresh'}
        </button>
      </div>

      <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 16, maxWidth: 920, lineHeight: 1.5 }}>
        Every request to <code style={{ background:'#000', padding:'1px 6px', borderRadius:4, color:'var(--cyan)' }}>POST /api/webhook/stripe</code> is logged here.
        Use this to diagnose why production subscriptions aren't auto-activating after payment.
        If you see "signature_failed" rows, the <strong>STRIPE_WEBHOOK_SECRET</strong> on this backend doesn't match the secret on the Stripe endpoint.
        If you see <em>zero</em> events after a real payment, Stripe is not delivering to this URL at all.
      </p>

      {diag && (
        <div data-testid="mf-webhook-diagnostics" className="glass-card" style={{ padding: 16, marginBottom: 20, maxWidth: 920 }}>
          <h3 style={{ fontSize: 14, fontWeight: 700, color: '#fff', marginBottom: 12 }}>Backend Configuration (this instance)</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, fontSize: 12 }}>
            <div><strong style={{ color: 'var(--text-secondary)' }}>Stripe mode:</strong> <span style={{ color: diag.stripe_api_key_mode === 'live' ? '#4caf50' : '#ff9800', fontWeight: 600 }}>{diag.stripe_api_key_mode}</span></div>
            <div><strong style={{ color: 'var(--text-secondary)' }}>API key fp:</strong> <code style={{ fontSize: 11 }}>{diag.stripe_api_key_fp || '—'}</code></div>
            <div><strong style={{ color: 'var(--text-secondary)' }}>Webhook secret:</strong> <span style={{ color: diag.webhook_secret_present ? '#4caf50' : '#ff4444', fontWeight: 600 }}>{diag.webhook_secret_present ? 'configured' : 'MISSING'}</span></div>
            <div><strong style={{ color: 'var(--text-secondary)' }}>Secret fp:</strong> <code style={{ fontSize: 11 }}>{diag.stripe_webhook_secret_fp || '—'}</code></div>
            <div><strong style={{ color: 'var(--text-secondary)' }}>Total events:</strong> {diag.total_events_logged}</div>
            <div><strong style={{ color: 'var(--text-secondary)' }}>Last 24h:</strong> {diag.events_last_24h} <span style={{ color: '#ff4444' }}>({diag.failed_events_last_24h} failed)</span></div>
          </div>
          <p style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 12, lineHeight: 1.5 }}>
            <AlertTriangle size={11} style={{ display: 'inline', verticalAlign: 'middle' }} /> Compare the
            "Secret fp" sha256 here against your production deployment. If they differ, the production env
            var is stale — update it in Emergent Support to match.
          </p>
        </div>
      )}

      {!events.length && !loading && (
        <div data-testid="mf-webhook-empty" className="glass-card" style={{ padding: 24, textAlign: 'center', color: 'var(--text-secondary)' }}>
          No webhook events logged yet. After a real Stripe webhook delivery, refresh this tab.
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {events.map(ev => (
          <div
            key={ev.id}
            data-testid={`mf-webhook-row-${ev.id}`}
            className="glass-card"
            style={{ padding: 12, fontSize: 12, borderLeft: `3px solid ${outcomeColor(ev.outcome)}` }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <span style={{ color: 'var(--text-secondary)', fontFamily: 'monospace' }}>{(ev.received_at || '').replace('T', ' ').split('.')[0]}</span>
                <span style={{ fontWeight: 700, color: '#fff' }}>{ev.event_type || '<no type>'}</span>
                <span style={{
                  padding: '2px 8px', borderRadius: 10, fontSize: 10, fontWeight: 700, textTransform: 'uppercase',
                  color: outcomeColor(ev.outcome),
                  background: 'rgba(255,255,255,0.06)',
                }}>{ev.outcome}</span>
                {ev.signature_verified ? (
                  <span style={{ color: '#4caf50', fontSize: 10, fontWeight: 600 }}>✓ sig ok</span>
                ) : (
                  <span style={{ color: '#ff4444', fontSize: 10, fontWeight: 600 }}>✗ sig {ev.parse_status}</span>
                )}
                {ev.action_taken && <span style={{ color: 'var(--cyan)', fontSize: 11 }}>{ev.action_taken}</span>}
              </div>
              <div style={{ display: 'flex', gap: 6 }}>
                {(ev.event_type === 'checkout.session.completed' || ev.event_type === 'invoice.paid') && ev.outcome !== 'success' && (
                  <button
                    data-testid={`mf-webhook-replay-${ev.id}`}
                    onClick={() => replay(ev)}
                    disabled={replaying === ev.id}
                    style={{
                      padding: '4px 10px', borderRadius: 6, border: '1px solid var(--cyan)',
                      background: 'transparent', color: 'var(--cyan)', fontSize: 11, fontWeight: 600,
                      cursor: replaying === ev.id ? 'wait' : 'pointer',
                    }}>
                    {replaying === ev.id ? 'Replaying…' : 'Replay'}
                  </button>
                )}
                <button
                  data-testid={`mf-webhook-expand-${ev.id}`}
                  onClick={() => setExpanded(expanded === ev.id ? null : ev.id)}
                  style={{
                    padding: '4px 10px', borderRadius: 6, border: '1px solid var(--dark-border)',
                    background: 'transparent', color: '#fff', fontSize: 11, fontWeight: 600, cursor: 'pointer',
                  }}>
                  {expanded === ev.id ? 'Hide' : 'Inspect'}
                </button>
              </div>
            </div>
            {ev.errors && ev.errors.length > 0 && (
              <div style={{ marginTop: 6, padding: 8, background: 'rgba(255,68,68,0.08)', borderRadius: 6, color: '#ff8c8c', fontSize: 11, fontFamily: 'monospace' }}>
                {ev.errors.map((er, i) => <div key={i}>⚠ {er}</div>)}
              </div>
            )}
            {expanded === ev.id && (
              <div data-testid={`mf-webhook-detail-${ev.id}`} style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid var(--dark-border)' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 10 }}>
                  <div><strong style={{ color: 'var(--text-secondary)' }}>Event id:</strong> <code>{ev.event_id || '—'}</code></div>
                  <div><strong style={{ color: 'var(--text-secondary)' }}>Session id:</strong> <code>{ev.session_id || '—'}</code></div>
                  <div><strong style={{ color: 'var(--text-secondary)' }}>User updated:</strong> <code>{ev.user_updated_id || '—'}</code></div>
                  <div><strong style={{ color: 'var(--text-secondary)' }}>Body size:</strong> {ev.body_size} bytes</div>
                </div>
                <details>
                  <summary style={{ cursor: 'pointer', color: 'var(--cyan)', fontSize: 11, marginBottom: 6 }}>Headers</summary>
                  <pre style={{ background: '#000', padding: 8, borderRadius: 6, fontSize: 10, color: '#9af', overflow: 'auto', maxHeight: 200 }}>{JSON.stringify(ev.headers, null, 2)}</pre>
                </details>
                <details>
                  <summary style={{ cursor: 'pointer', color: 'var(--cyan)', fontSize: 11, marginTop: 6, marginBottom: 6 }}>Raw body</summary>
                  <pre style={{ background: '#000', padding: 8, borderRadius: 6, fontSize: 10, color: '#fc8', overflow: 'auto', maxHeight: 300 }}>{ev.body_preview || '<empty>'}</pre>
                </details>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
